/**
 * MP4 faststart normalization: move the `moov` atom before `mdat` so
 * `loadedmetadata` needs only the file head (~100s of KB) instead of
 * tail-chasing across the whole file on slow links.
 *
 * Lossless atom rearrangement — no re-encode, no new dependency. Runs in the
 * browser between download and the Azure PUT (see videoStorageService /
 * uploadedAssetStorageService). Detect-first: already-fast, fragmented, or
 * unparseable files pass through byte-identical and never fail an upload.
 */

export type FaststartOutcome =
  | 'moved-moov'
  | 'already-fast'
  | 'no-moov'
  | 'no-mdat'
  | 'fragmented'
  | 'unparseable';

export interface FaststartResult {
  /** Rewritten bytes, or the original buffer when `applied` is false. */
  buffer: ArrayBuffer;
  applied: boolean;
  reason: FaststartOutcome;
  /** Top-level atom map for console diagnosis, e.g. `ftyp@0 mdat@32 moov@8388608`. */
  atomMap: string;
}

interface Box {
  type: string;
  start: number;
  size: number;
  headerSize: number;
}

/** Container boxes worth descending into for chunk-offset tables. */
const CONTAINERS = new Set(['moov', 'trak', 'mdia', 'minf', 'stbl', 'edts']);

const readBoxHeader = (view: DataView, offset: number): Box | null => {
  if (offset + 8 > view.byteLength) return null;
  let size = view.getUint32(offset);
  const type = String.fromCharCode(
    view.getUint8(offset + 4),
    view.getUint8(offset + 5),
    view.getUint8(offset + 6),
    view.getUint8(offset + 7)
  );
  let headerSize = 8;
  if (size === 1) {
    if (offset + 16 > view.byteLength) return null;
    size = view.getUint32(offset + 8) * 2 ** 32 + view.getUint32(offset + 12);
    headerSize = 16;
  } else if (size === 0) {
    // Size 0 means "to end of file" (common for recorded mdat).
    size = view.byteLength - offset;
  }
  if (!Number.isSafeInteger(size) || size < headerSize) return null;
  if (offset + size > view.byteLength) return null;
  return { type, start: offset, size, headerSize };
};

const readBoxes = (view: DataView, start: number, end: number): Box[] | null => {
  const boxes: Box[] = [];
  let offset = start;
  while (offset < end) {
    const box = readBoxHeader(view, offset);
    if (!box) return null;
    boxes.push(box);
    offset += box.size;
  }
  return offset === end ? boxes : null;
};

/**
 * Shift every chunk offset inside stbl tables that points into the `mdat`
 * payload by `delta` (the moov box moves earlier by exactly its own size, so
 * every mdat byte shifts forward by that amount). Returns false when the box
 * structure is not fully understood — the caller then passes the file through
 * untouched rather than moving moov with unpatched offsets.
 */
const patchChunkOffsets = (
  view: DataView,
  start: number,
  end: number,
  delta: number,
  mdatDataStart: number,
  mdatDataEnd: number
): boolean => {
  const boxes = readBoxes(view, start, end);
  if (!boxes) return false;
  for (const box of boxes) {
    if (box.type === 'stco' || box.type === 'co64') {
      const entrySize = box.type === 'stco' ? 4 : 8;
      // Full box: size(4) type(4) version(1) flags(3) entry_count(4).
      const count = view.getUint32(box.start + 12);
      if (box.start + 16 + count * entrySize > box.start + box.size) {
        return false;
      }
      for (let i = 0; i < count; i++) {
        const at = box.start + 16 + i * entrySize;
        if (entrySize === 4) {
          const value = view.getUint32(at);
          if (value >= mdatDataStart && value < mdatDataEnd) {
            view.setUint32(at, value + delta);
          }
        } else {
          const value = view.getBigUint64(at);
          if (value >= BigInt(mdatDataStart) && value < BigInt(mdatDataEnd)) {
            view.setBigUint64(at, value + BigInt(delta));
          }
        }
      }
    } else if (CONTAINERS.has(box.type)) {
      const ok = patchChunkOffsets(
        view,
        box.start + box.headerSize,
        box.start + box.size,
        delta,
        mdatDataStart,
        mdatDataEnd
      );
      if (!ok) return false;
    }
  }
  return true;
};

/**
 * Return moov-first bytes for MP4-family content. The input is never mutated;
 * when no rewrite applies, the original buffer is returned untouched.
 */
export const ensureMp4Faststart = (input: ArrayBuffer): FaststartResult => {
  const bytes = new Uint8Array(input);
  const view = new DataView(input);
  const fail = (reason: FaststartOutcome, atomMap: string): FaststartResult => ({
    buffer: input,
    applied: false,
    reason,
    atomMap,
  });

  const top = readBoxes(view, 0, bytes.byteLength);
  if (!top) return fail('unparseable', '');
  const atomMap = top.map((box) => `${box.type}@${box.start}`).join(' ');
  if (top.some((box) => box.type === 'moof' || box.type === 'sidx')) {
    return fail('fragmented', atomMap);
  }
  const moov = top.find((box) => box.type === 'moov');
  const mdat = top.find((box) => box.type === 'mdat');
  if (!moov) return fail('no-moov', atomMap);
  if (!mdat) return fail('no-mdat', atomMap);
  if (moov.start < mdat.start) return fail('already-fast', atomMap);

  // Slow layout: everything keeps its relative order except moov, which moves
  // to just before mdat. mdat payload shifts forward by exactly moov.size.
  const delta = moov.size;
  const mdatDataStart = mdat.start + mdat.headerSize;
  const mdatDataEnd = mdat.start + mdat.size;
  const moovCopy = bytes.slice(moov.start, moov.start + moov.size);
  const patched = patchChunkOffsets(
    new DataView(moovCopy.buffer, moovCopy.byteOffset, moovCopy.byteLength),
    moov.headerSize,
    moovCopy.byteLength,
    delta,
    mdatDataStart,
    mdatDataEnd
  );
  if (!patched) return fail('unparseable', atomMap);

  const out = new Uint8Array(bytes.byteLength);
  let pos = 0;
  for (const box of top) {
    if (box.type === 'moov') continue;
    if (box.type === 'mdat') {
      out.set(moovCopy, pos);
      pos += moovCopy.byteLength;
    }
    out.set(bytes.subarray(box.start, box.start + box.size), pos);
    pos += box.size;
  }
  // Defensive: sizes must reconcile exactly (same boxes, new order).
  if (pos !== bytes.byteLength) return fail('unparseable', atomMap);

  // Copy into a fresh ArrayBuffer so the result never shares memory.
  const buffer = new ArrayBuffer(out.byteLength);
  new Uint8Array(buffer).set(out);
  return { buffer, applied: true, reason: 'moved-moov', atomMap };
};
